import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, useMediaQuery, useTheme } from '@mui/material'
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { DetailButton } from '../../../components/common/detail'
import { extractActionsApi } from '../../../api/actionImportApi'
import { extractErrorMessage } from '../../../utils/errorHandler'
import ContextStep from './ContextStep'
import ResultStep from './ResultStep'
import ReviewStep from './ReviewStep'
import SourceStep from './SourceStep'
import { jiraImportAdapter } from './jiraImportAdapter'
import { ImportContext, ImportRow, RowErrors, contextFromIds, validateRow } from './importRowModel'
import { runImport } from './runImport'
import { WIZARD_STEPS, WizardStep, extractionKey, useImportWizard } from './useImportWizard'
import '../../plan/ImportRisksWizard.css'
import './ImportActionsWizard.css'

type ImportActionsWizardProps = {
  open: boolean
  onClose: () => void
  /** Called once per import run that created at least one action. */
  onImported: (created: number, failed: number) => void
  /** When set, every imported task starts in this mission and the field stays locked. */
  missionId?: string
  /** When set, every imported task starts in this project and the field stays locked. */
  projectId?: string
}

function describeExtractError(error: unknown): string {
  const data = (error as { response?: { data?: { details?: unknown } } })?.response?.data
  const details = data?.details
  const detail =
    typeof details === 'string'
      ? details
      : details && typeof details === 'object' && 'detail' in details
        ? String((details as { detail: unknown }).detail)
        : ''
  const base = extractErrorMessage(error)
  return detail && detail !== base ? `${base} — ${detail}` : base
}

export default function ImportActionsWizard({
  open,
  onClose,
  onImported,
  missionId,
  projectId,
}: ImportActionsWizardProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [state, dispatch] = useImportWizard()
  const [confirmClose, setConfirmClose] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const stopRef = useRef(false)
  const preset = useMemo(
    () => contextFromIds(missionId, projectId),
    [missionId, projectId]
  )
  const lockMission = Boolean(missionId)
  const lockProject = Boolean(projectId)

  const { step, source, file, extraction, rows, run } = state

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort()
      stopRef.current = true
      dispatch({ type: 'reset', context: preset })
      setConfirmClose(false)
      return
    }
    dispatch({ type: 'setContext', context: preset })
  }, [open, preset, dispatch])

  const errorsById = useMemo(() => {
    const map: Record<string, RowErrors> = {}
    rows.forEach((row: ImportRow) => {
      map[row.id] = validateRow(row)
    })
    return map
  }, [rows])

  const errorCount = useMemo(
    () => rows.filter((r) => Object.keys(errorsById[r.id]).length).length,
    [rows, errorsById]
  )

  const onLabels = useCallback(
    (labels: Record<string, string>) => dispatch({ type: 'mergeLabels', labels }),
    [dispatch]
  )

  const startExtraction = useCallback(async () => {
    if (!source || source === 'jira' || !file) return
    const key = extractionKey(source, file)
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    dispatch({ type: 'extractStart', key })
    try {
      const response = await extractActionsApi({ source, file, signal: controller.signal })
      dispatch({ type: 'extractSuccess', key, response })
    } catch (error) {
      if (controller.signal.aborted) return
      dispatch({ type: 'extractError', key, error: describeExtractError(error) })
    }
  }, [dispatch, file, source])

  const connectJira = useCallback(async () => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    dispatch({ type: 'jiraConnecting' })
    try {
      const { issues } = await jiraImportAdapter.connect(controller.signal)
      dispatch({ type: 'extractSuccess', key: 'jira', response: issues })
    } catch (error) {
      if (controller.signal.aborted) return
      dispatch({ type: 'jiraFailed', error: describeExtractError(error) })
    }
  }, [dispatch])

  const startImport = async () => {
    const toImport = rows.filter((r) => !Object.keys(errorsById[r.id]).length)
    if (!toImport.length || errorCount) return
    stopRef.current = false
    dispatch({ type: 'importStart', total: toImport.length })
    let created = 0
    let failed = 0
    await runImport(
      toImport,
      (outcome) => {
        if (outcome.ok) created += 1
        else failed += 1
        dispatch(
          outcome.ok
            ? { type: 'importOutcome', rowId: outcome.rowId, ok: true }
            : { type: 'importOutcome', rowId: outcome.rowId, ok: false, message: outcome.message }
        )
      },
      () => stopRef.current
    )
    if (stopRef.current) return
    dispatch({ type: 'importDone' })
    if (created) onImported(created, failed)
    if (!failed) onClose()
  }

  const dirty = Boolean(source) && !(step === 'result' && run.status === 'done')
  const requestClose = () => {
    if (run.status === 'running') return
    if (dirty) setConfirmClose(true)
    else onClose()
  }

  const goNext = () => {
    if (step === 'source') {
      dispatch({ type: 'goTo', step: 'context' })
      return
    }
    if (step === 'context') {
      const key = extractionKey(source, file)
      const cached = extraction.key === key && extraction.status === 'success'
      if (cached) dispatch({ type: 'applyContextToRows' })
      dispatch({ type: 'goTo', step: 'review' })
      if (!cached && source !== 'jira') startExtraction()
    }
  }

  const goBack = () => {
    const index = WIZARD_STEPS.indexOf(step)
    if (index > 0) dispatch({ type: 'goTo', step: WIZARD_STEPS[index - 1] })
  }

  const stepIndex = WIZARD_STEPS.indexOf(step)
  const sourceReady =
    source === 'jira' ? state.jira.status === 'connected' : Boolean(source && file)

  const footMeta = (() => {
    if (step === 'source') return sourceReady ? null : t('actionImport.foot.pickSource')
    if (step === 'review' && extraction.status !== 'success' && extraction.status !== 'error') {
      return t('actionImport.review.extracting')
    }
    if (step === 'review' && extraction.status === 'success') {
      if (!rows.length) return t('actionImport.foot.noRows')
      if (errorCount) return t('actionImport.foot.blocked', { count: errorCount })
      return t('actionImport.foot.ready', { count: rows.length })
    }
    return null
  })()

  const stepLabels: Record<WizardStep, string> = {
    source: t('actionImport.steps.source'),
    context: t('actionImport.steps.context'),
    review: t('actionImport.steps.review'),
    result: t('actionImport.steps.result'),
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown' && (event as React.KeyboardEvent).defaultPrevented) return
        requestClose()
      }}
      fullWidth
      maxWidth={false}
      fullScreen={fullScreen}
      aria-labelledby="action-import-title"
      PaperProps={{ className: 'incyia-modal-paper import-risks-wizard-paper action-import-paper' }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(6, 21, 36, 0.55)' } }}
    >
      <div className="incyia-modal-head">
        <div className="incyia-modal-icon" aria-hidden>
          <CloudUploadOutlined sx={{ fontSize: 22 }} />
        </div>
        <div>
          <h3 id="action-import-title">{t('actionImport.title')}</h3>
          <p>{t(`actionImport.subtitles.${step}`)}</p>
        </div>
        <button
          type="button"
          className="incyia-modal-x"
          onClick={requestClose}
          disabled={run.status === 'running'}
          aria-label={t('actionImport.close')}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <nav className="import-risks-wizard-stepper action-import-stepper" aria-label={t('actionImport.steps.label')}>
        {WIZARD_STEPS.map((s, index) => {
          const active = index === stepIndex
          const done = index < stepIndex
          return (
            <div
              key={s}
              className={`import-risks-wizard-step ${active ? 'is-active' : done ? 'is-done' : ''}`.trim()}
              aria-current={active ? 'step' : undefined}
            >
              <span className="import-risks-wizard-step-index" aria-hidden>
                {done ? '✓' : String(index + 1).padStart(2, '0')}
              </span>
              <span>
                <span className="import-risks-wizard-step-meta">
                  {t('actionImport.steps.of', { step: index + 1, total: WIZARD_STEPS.length })}
                </span>
                <span className="import-risks-wizard-step-title">{stepLabels[s]}</span>
              </span>
            </div>
          )
        })}
      </nav>

      <div className="import-risks-wizard-body action-import-body">
        {step === 'source' ? (
          <SourceStep
            source={source}
            file={file}
            jira={state.jira}
            jiraIssueCount={extraction.key === 'jira' ? extraction.total : undefined}
            onSelectSource={(s) => dispatch({ type: 'selectSource', source: s })}
            onFile={(f) => dispatch({ type: 'setFile', file: f })}
            onConnectJira={connectJira}
          />
        ) : null}
        {step === 'context' ? (
          <ContextStep
            value={state.context}
            onChange={(context: ImportContext) =>
              dispatch({
                type: 'setContext',
                context: {
                  ...context,
                  MissionId: missionId || context.MissionId,
                  ProjetId: projectId || context.ProjetId,
                },
              })
            }
            onLabels={onLabels}
            rowCount={extraction.status === 'success' ? rows.length : undefined}
            lockMission={lockMission}
            lockProject={lockProject}
          />
        ) : null}
        {step === 'review' ? (
          <ReviewStep
            extraction={extraction}
            rows={rows}
            errorsById={errorsById}
            labels={state.labels}
            deletedCount={state.lastDeleted?.rows.length ?? 0}
            onRetryExtract={source === 'jira' ? connectJira : startExtraction}
            onUpdateRow={(id, patch) =>
              dispatch({
                type: 'updateRow',
                id,
                patch: {
                  ...patch,
                  ...(missionId ? { MissionId: missionId } : {}),
                  ...(projectId ? { ProjetId: projectId } : {}),
                },
              })
            }
            onPatchRows={(ids, patch) =>
              dispatch({
                type: 'patchRows',
                ids,
                patch: {
                  ...patch,
                  ...(missionId ? { MissionId: missionId } : {}),
                  ...(projectId ? { ProjetId: projectId } : {}),
                },
              })
            }
            onDeleteRows={(ids) => dispatch({ type: 'deleteRows', ids })}
            onUndoDelete={() => dispatch({ type: 'undoDelete' })}
            onAddRow={() => dispatch({ type: 'addRow' })}
            onLabels={onLabels}
            lockMission={lockMission}
            lockProject={lockProject}
            preset={preset}
          />
        ) : null}
        {step === 'result' ? <ResultStep run={run} /> : null}
      </div>

      <div className="import-risks-wizard-foot">
        <span className="import-risks-wizard-foot-meta" aria-live="polite">
          {footMeta}
        </span>
        <div className="import-risks-wizard-foot-actions">
          {step === 'result' ? (
            run.status === 'done' ? (
              <>
                <DetailButton variant="ghost" onClick={onClose}>
                  {t('actionImport.close')}
                </DetailButton>
                {run.failed.length ? (
                  <DetailButton onClick={() => dispatch({ type: 'retryFailed' })}>
                    {t('actionImport.result.fixFailed', { count: run.failed.length })}
                  </DetailButton>
                ) : null}
              </>
            ) : null
          ) : (
            <>
              <DetailButton variant="ghost" onClick={stepIndex === 0 ? requestClose : goBack}>
                {stepIndex === 0 ? t('buttons.cancel', { defaultValue: 'Cancel' }) : t('Back')}
              </DetailButton>
              {step === 'review' ? (
                <DetailButton
                  onClick={startImport}
                  disabled={extraction.status !== 'success' || !rows.length || errorCount > 0}
                >
                  {rows.length
                    ? t('actionImport.importN', { count: rows.length })
                    : t('actionImport.trigger')}
                </DetailButton>
              ) : (
                <DetailButton onClick={goNext} disabled={step === 'source' && !sourceReady}>
                  {t('actionImport.next')}
                </DetailButton>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: 'incyia-modal-paper' }}
      >
        <div className="incyia-modal-head">
          <div>
            <h3>{t('actionImport.confirmClose.title')}</h3>
            <p>{t('actionImport.confirmClose.message')}</p>
          </div>
        </div>
        <div className="incyia-modal-foot">
          <DetailButton variant="ghost" onClick={() => setConfirmClose(false)}>
            {t('actionImport.confirmClose.stay')}
          </DetailButton>
          <DetailButton
            variant="danger"
            onClick={() => {
              setConfirmClose(false)
              onClose()
            }}
          >
            {t('actionImport.confirmClose.discard')}
          </DetailButton>
        </div>
      </Dialog>
    </Dialog>
  )
}

type ImportTasksButtonProps = {
  missionId?: string
  projectId?: string
  onImported: (created: number, failed: number) => void
}

/** Opens the import wizard. Use next to every Add task action. */
export function ImportTasksButton({ missionId, projectId, onImported }: ImportTasksButtonProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <>
      <DetailButton variant="ghost" onClick={() => setOpen(true)}>
        <CloudUploadOutlined sx={{ fontSize: 16 }} />
        {t('actionImport.trigger')}
      </DetailButton>
      <ImportActionsWizard
        open={open}
        onClose={() => setOpen(false)}
        onImported={onImported}
        missionId={missionId}
        projectId={projectId}
      />
    </>
  )
}
